import { Component, computed, effect, ElementRef, inject, input, OnDestroy, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	ArcElement,
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	ChartConfiguration,
	DoughnutController,
	Legend,
	LinearScale,
	Tooltip
} from 'chart.js';
import { ThemeService } from '../../../../core/services/theme.service';
import { DashboardChartConfig } from '../../../../core/utilities/dashboard-chart.utilities';

Chart.register(
	DoughnutController,
	ArcElement,
	BarController,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend
);

const donutCenterTotalPlugin = {
	id: 'donutCenterTotal',
	afterDatasetsDraw(chart: Chart) {
		const dataset = chart.data.datasets[0];
		if (!dataset) return;
		const total = (dataset.data as number[]).reduce(
			(sum, value) => sum + (typeof value === 'number' ? value : 0),
			0
		);
		const { ctx, chartArea } = chart;
		const centerX = (chartArea.left + chartArea.right) / 2;
		const centerY = (chartArea.top + chartArea.bottom) / 2;
		const color = (chart.options.color as string | undefined) ?? '#495057';

		ctx.save();
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = color;
		ctx.font = '600 13px sans-serif';
		ctx.fillText('Total', centerX, centerY - 10);
		ctx.font = '700 18px sans-serif';
		ctx.fillText(String(total), centerX, centerY + 10);
		ctx.restore();
	}
};

@Component({
	selector: 'ms-dashboard-chart',
	imports: [],
	templateUrl: './dashboard-chart.component.html',
	styleUrl: './dashboard-chart.component.scss'
})
export class DashboardChartComponent implements OnDestroy {

	public title = input.required<string>();
	public config = input<DashboardChartConfig | undefined>(undefined);
	public isLoading = input<boolean>(false);
	public height = input<string>('280');

	public effectiveHeight = computed<number>(() => {
		const fromConfig = this.config()?.height;
		if (fromConfig !== undefined) return fromConfig;
		const parsed = Number(this.height());
		return Number.isFinite(parsed) ? parsed : 280;
	});

	private canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvasRef');
	private themeService = inject(ThemeService);
	private chart?: Chart;

	constructor() {
		effect(() => {
			const cfg = this.config();
			const canvas = this.canvasRef()?.nativeElement;
			const loading = this.isLoading();

			if (!canvas || loading || !cfg) {
				this.chart?.destroy();
				this.chart = undefined;
				return;
			}

			this.renderChart(canvas, cfg);
		});

		this.themeService.currentTheme$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.applyThemeColors());
	}

	public ngOnDestroy(): void {
		this.chart?.destroy();
	}

	private renderChart(canvas: HTMLCanvasElement, cfg: DashboardChartConfig): void {
		this.chart?.destroy();

		const plugins = cfg.showCenterTotal ? [donutCenterTotalPlugin] : [];
		this.chart = new Chart(canvas, {
			type: cfg.type,
			data: cfg.data,
			options: cfg.options,
			plugins
		} as ChartConfiguration);
	}

	private applyThemeColors(): void {
		if (!this.chart) return;
		const foreground = this.themeService.isDarkThemeActive() ? '#adb5bd' : '#495057';
		this.chart.options.color = foreground;

		const legendLabels = this.chart.options.plugins?.legend?.labels;
		if (legendLabels) {
			(legendLabels as { color?: string }).color = foreground;
		}

		const scales = this.chart.options.scales;
		if (scales) {
			for (const scale of Object.values(scales)) {
				const ticks = (scale as { ticks?: { color?: string } } | undefined)?.ticks;
				if (ticks) ticks.color = foreground;
			}
		}

		this.chart.update('none');
	}
}
